import React, { useMemo } from 'react';
import { Button, Tag, Tooltip } from 'antd';
import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import { ListBase, ListProps } from 'react-admin';
import { Link, useHistory } from 'react-router-dom';
import { NetOrcaTableListV1, TableListColumn } from '@src/components/Common/NetOrcaTableList';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';
import './Messages.css';

const RESOURCE = 'serviceowner_messages';

/** how many services a row names before it says how many more there are */
const SCOPES_SHOWN = 3;

/**
 * One message under the shared column header. The whole row opens it: a
 * message is a short record that is only ever read in order to change it, so
 * there is nothing for a row to offer that its own page does not — deleting
 * included.
 */
const MessageRow = ({ record }: { record: any }) => {
    const history = useHistory();
    const open = () => history.push(`/${RESOURCE}/${record.id}/edit`);

    const scopes: any[] = record.scopes || [];
    const shown = scopes.slice(0, SCOPES_SHOWN);
    const rest = scopes.length - shown.length;

    return (
        <div
            className={`msg-row${record.has_expired ? ' msg-row--expired' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={`Open message ${record.id}`}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            <div className="msg-cell msg-cell--id" data-label="ID">
                {record.id}
            </div>

            <div className="msg-cell msg-text" data-label="Message">
                {record.description}
            </div>

            {/* Service names read as names, the way they do on the service
                items list — not as tags. A tag is for a value from a fixed set
                you might filter by; a service name is neither, and a row of
                three of them boxed up looked like state. */}
            <div className="msg-cell" data-label="Services">
                {scopes.length === 0 ? (
                    <span className="msg-cell--muted">All services</span>
                ) : (
                    <span className="msg-scopes">
                        {shown.map((entry: any) => (
                            <span className="msg-scope" key={entry.scope?.id ?? entry.scope?.name}>
                                {entry.scope?.name}
                            </span>
                        ))}
                        {rest > 0 && (
                            <Tooltip title={scopes.map((entry: any) => entry.scope?.name).join(', ')}>
                                <span className="msg-scope msg-scope--more">+{rest}</span>
                            </Tooltip>
                        )}
                    </span>
                )}
            </div>

            {/* a message with no expiry stands until it is deleted, which is
                worth saying rather than leaving the cell blank */}
            <div className="msg-cell msg-cell--muted" data-label="Expires">
                {record.expiry_date ? (
                    <Tooltip title={formatDateAndTime(record.expiry_date)}>
                        <span>
                            {record.has_expired ? (
                                <Tag color="default">Expired</Tag>
                            ) : (
                                formatDateAndTimeFromNow(record.expiry_date)
                            )}
                        </span>
                    </Tooltip>
                ) : (
                    'Never'
                )}
            </div>

            <RightOutlined className="msg-chev" aria-hidden="true" />
        </div>
    );
};

const MessageListView = () => {
    const columns: TableListColumn[] = useMemo(
        () => [
            { label: 'ID', dataCy: 'column-id' },
            { label: 'Message', dataCy: 'column-message' },
            { label: 'Services', dataCy: 'column-services' },
            { label: 'Expires', dataCy: 'column-expires' },
            // the last track is the create button's, over each row's chevron
        ],
        [],
    );

    return (
        <NetOrcaTableListV1
            className="netorca-msg"
            dataCy="message-result-list"
            // nothing on a message is worth narrowing by: the API filters on id
            // alone, and a list you can read at a glance has no use for it
            fields={[]}
            columns={columns}
            template="var(--msg-cols)"
            emptyText="No messages published yet"
            noun="message"
            headerTrailing={
                <Link to={`/${RESOURCE}/create`}>
                    <Button className="msg-create" type="primary" size="small" icon={<PlusOutlined />}>
                        New message
                    </Button>
                </Link>
            }
            renderRow={(record) => <MessageRow key={record.id} record={record} />}
        />
    );
};

/**
 * The notices this team publishes to the consumers of its services. Each row is
 * the message itself, who will see it and when it stops being shown.
 */
const MessageList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase sort={{ field: 'id', order: 'DESC' }} perPage={25} {...props}>
        <MessageListView />
    </ListBase>
);

export default MessageList;
